import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API_URL, formatApiError } from '../../utils/api';
import { authHeaders, StatusChip, fmtDate, Pager } from './instrShared';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import {
  Spinner, MagnifyingGlass, ArrowDown, ArrowUp, ClockCounterClockwise, Eye, Package,
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const PAGE_SIZE = 8;

export const InstrStockTab = () => {
  const [stock, setStock] = useState([]);
  const [movements, setMovements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [stockPage, setStockPage] = useState(1);
  const [movePage, setMovePage] = useState(1);
  const [stores, setStores] = useState([]);
  const [viewTool, setViewTool] = useState(null);

  const load = useCallback(async () => {
    try {
      const [s, m] = await Promise.all([
        axios.get(`${API_URL}/api/instrumentation/stock`, { headers: authHeaders() }),
        axios.get(`${API_URL}/api/instrumentation/stock/movements?limit=200`, { headers: authHeaders() }),
      ]);
      setStock(Array.isArray(s.data) ? s.data : []);
      setMovements(Array.isArray(m.data) ? m.data : []);
    } catch (e) {
      toast.error(formatApiError(e, 'Failed to load stock'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    axios.get(`${API_URL}/api/instrumentation/stores`, { headers: authHeaders() })
      .then((res) => setStores(Array.isArray(res.data) ? res.data : []))
      .catch(() => { /* store list optional until stock-out */ });
  }, []);

  const ql = search.trim().toLowerCase();
  const filtered = ql
    ? stock.filter((s) => s.name.toLowerCase().includes(ql) || s.category.toLowerCase().includes(ql))
    : stock;
  const stockTotal = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const stockSafe = Math.min(stockPage, stockTotal);
  const stockPaged = filtered.slice((stockSafe - 1) * PAGE_SIZE, stockSafe * PAGE_SIZE);

  const moveTotal = Math.max(1, Math.ceil(movements.length / PAGE_SIZE));
  const moveSafe = Math.min(movePage, moveTotal);
  const movePaged = movements.slice((moveSafe - 1) * PAGE_SIZE, moveSafe * PAGE_SIZE);

  return (
    <div className="space-y-5" data-testid="instr-stock-tab">
      <div className="relative">
        <MagnifyingGlass size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#78716C]" />
        <Input
          value={search}
          onChange={(e) => { setSearch(e.target.value); setStockPage(1); }}
          placeholder="Search stock by tool or category…"
          className="h-10 pl-9 text-base w-full sm:w-[380px] bg-white"
          data-testid="stock-search"
        />
      </div>

      {loading ? (
        <div className="py-20 text-center"><Spinner size={22} className="animate-spin text-[#0A2540] inline" /></div>
      ) : (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-5 items-stretch">
          {/* Stock table */}
          <div className="bg-white border border-[#EDE6D8] rounded-2xl overflow-hidden flex flex-col" data-testid="stock-list">
            <div className="flex items-center gap-2.5 px-5 pt-5 pb-3">
              <span className="w-8 h-8 rounded-lg flex items-center justify-center bg-[#0A2540]/10 text-[#0A2540]">
                <Package size={16} weight="fill" />
              </span>
              <p className="text-[13px] uppercase tracking-[0.18em] text-[#0A2540] font-semibold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                Stock Levels
              </p>
            </div>
            <div className="grid grid-cols-[1.4fr_1fr_90px_120px_60px] gap-2 items-center px-5 py-3 bg-[#FCFAF6] text-[11px] uppercase tracking-widest text-[#78716C] font-semibold">
              <span>Tool</span>
              <span>Category</span>
              <span>On Hand</span>
              <span>Status</span>
              <span />
            </div>
            {filtered.length === 0 ? (
              <p className="text-sm text-[#78716C] py-12 text-center">
                {stock.length === 0 ? 'No tools in the catalogue yet. Super Admin can add tools under Configurations → Instrumentation.' : 'No tools match your search.'}
              </p>
            ) : (
              <div className="divide-y divide-[#EDE6D8]">
                {stockPaged.map((s) => (
                  <div key={s.tool_id} className="grid grid-cols-[1.4fr_1fr_90px_120px_60px] gap-2 items-center px-5 min-h-[64px]" data-testid={`stock-row-${s.tool_id}`}>
                    <span className="text-base font-medium text-[#1C1917] truncate">{s.name}</span>
                    <span className="text-sm text-[#78716C] truncate">{s.category}</span>
                    <span className="text-lg font-mono font-semibold text-[#0A2540]">{s.quantity}</span>
                    <StatusChip size="md" status={s.status === 'ok' ? 'ok' : s.status} label={s.status === 'ok' ? 'In stock' : undefined} />
                    <button
                      type="button"
                      onClick={() => setViewTool(s)}
                      className="text-[#0A2540] hover:bg-[#0A2540]/5 rounded-md p-2 transition justify-self-end"
                      title="View & manage stock"
                      data-testid={`stock-view-${s.tool_id}`}
                    >
                      <Eye size={18} />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <div className="mt-auto">
              <Pager page={stockSafe} totalPages={stockTotal} count={filtered.length} label="tools" onPage={setStockPage} testid="stock-pagination" />
            </div>
          </div>

          {/* Recent movements */}
          <div className="bg-white border border-[#EDE6D8] rounded-2xl overflow-hidden flex flex-col" data-testid="stock-movements-panel">
            <div className="flex items-center gap-2.5 px-5 pt-5 pb-3">
              <span className="w-8 h-8 rounded-lg flex items-center justify-center bg-[#0A2540]/10 text-[#0A2540]">
                <ClockCounterClockwise size={16} weight="fill" />
              </span>
              <p className="text-[13px] uppercase tracking-[0.18em] text-[#0A2540] font-semibold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                Recent Movements
              </p>
            </div>
            <div className="grid grid-cols-[1fr_120px] gap-2 items-center px-5 py-3 bg-[#FCFAF6] text-[11px] uppercase tracking-widest text-[#78716C] font-semibold">
              <span>Movement</span>
              <span className="text-right">Date</span>
            </div>
            {movements.length === 0 ? (
              <p className="text-sm text-[#78716C] py-12 text-center">No stock movements yet.</p>
            ) : (
              <div className="divide-y divide-[#EDE6D8]">
                {movePaged.map((m) => (
                  <div key={m.id} className="flex items-center justify-between gap-3 px-5 min-h-[64px]" data-testid={`stock-move-${m.id}`}>
                    <div className="min-w-0">
                      <p className="text-base font-medium text-[#1C1917] truncate">
                        {m.tool_name}
                        <span className={`ml-2 font-mono font-semibold ${m.direction === 'in' ? 'text-[#047857]' : 'text-[#B91C1C]'}`}>
                          {m.direction === 'in' ? '+' : '−'}{m.quantity}
                        </span>
                        <span className="ml-2 text-xs font-mono text-[#78716C]">bal {m.balance_after}</span>
                      </p>
                      <p className="text-xs text-[#78716C] truncate">
                        {m.by}{m.direction === 'out' && m.store_name ? ` → ${m.store_name}` : ''}{m.note ? ` — ${m.note}` : ''}
                      </p>
                    </div>
                    <span className="text-sm font-mono text-[#78716C] shrink-0">{fmtDate(m.created_at)}</span>
                  </div>
                ))}
              </div>
            )}
            <div className="mt-auto">
              <Pager page={moveSafe} totalPages={moveTotal} count={movements.length} label="movements" onPage={setMovePage} testid="stock-move-pagination" />
            </div>
          </div>
        </div>
      )}

      <StockDetailDialog
        tool={viewTool}
        stores={stores}
        onClose={() => setViewTool(null)}
        onChanged={(updated) => {
          setViewTool((prev) => (prev ? { ...prev, quantity: updated.balance_after, status: updated.status } : prev));
          load();
        }}
      />
    </div>
  );
};

function StockDetailDialog({ tool, stores, onClose, onChanged }) {
  const [toolMoves, setToolMoves] = useState([]);
  const [movesLoading, setMovesLoading] = useState(false);
  const [moveMode, setMoveMode] = useState(null); // 'in' | 'out' | null
  const [qty, setQty] = useState('');
  const [note, setNote] = useState('');
  const [destStoreId, setDestStoreId] = useState('');
  const [saving, setSaving] = useState(false);

  const loadMoves = useCallback(async () => {
    if (!tool) return;
    setMovesLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/instrumentation/stock/movements?tool_id=${tool.tool_id}&limit=20`, { headers: authHeaders() });
      setToolMoves(Array.isArray(res.data) ? res.data : []);
    } catch {
      setToolMoves([]);
    } finally {
      setMovesLoading(false);
    }
  }, [tool?.tool_id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setMoveMode(null);
    setQty('');
    setNote('');
    setDestStoreId('');
    loadMoves();
  }, [tool?.tool_id, loadMoves]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async () => {
    const q = parseInt(qty, 10);
    if (!Number.isFinite(q) || q < 1) { toast.error('Enter a quantity of at least 1'); return; }
    if (moveMode === 'out' && !destStoreId) { toast.error('Select the store this stock is issued to'); return; }
    setSaving(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/instrumentation/stock/movements`, {
        tool_id: tool.tool_id,
        direction: moveMode,
        quantity: q,
        note,
        ...(moveMode === 'out' ? { store_id: destStoreId } : {}),
      }, { headers: authHeaders() });
      toast.success(`${tool.name}: ${moveMode === 'in' ? '+' : '−'}${q} → ${data.balance_after} on hand`);
      setMoveMode(null);
      setQty('');
      setNote('');
      setDestStoreId('');
      onChanged(data);
      loadMoves();
    } catch (e) {
      toast.error(formatApiError(e, 'Failed to record movement'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!tool} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="stock-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {tool?.name}
            {tool && <StatusChip status={tool.status === 'ok' ? 'ok' : tool.status} label={tool.status === 'ok' ? 'In stock' : undefined} />}
          </DialogTitle>
          <DialogDescription>
            {tool?.category} · On hand: <span className="font-mono font-semibold">{tool?.quantity}</span>
          </DialogDescription>
        </DialogHeader>

        {/* Actions */}
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setMoveMode(moveMode === 'in' ? null : 'in')}
            className={`inline-flex items-center gap-1.5 text-xs font-semibold rounded-lg px-3 py-1.5 transition border ${moveMode === 'in' ? 'bg-[#047857] text-white border-[#047857]' : 'text-[#047857] border-[#E7F5EC] bg-[#E7F5EC]/60 hover:bg-[#E7F5EC]'}`}
            data-testid="stock-detail-in-btn"
          >
            <ArrowDown size={12} weight="bold" /> Stock In
          </button>
          <button
            type="button"
            onClick={() => setMoveMode(moveMode === 'out' ? null : 'out')}
            disabled={(tool?.quantity ?? 0) <= 0}
            className={`inline-flex items-center gap-1.5 text-xs font-semibold rounded-lg px-3 py-1.5 transition border disabled:opacity-40 disabled:cursor-not-allowed ${moveMode === 'out' ? 'bg-[#B91C1C] text-white border-[#B91C1C]' : 'text-[#B91C1C] border-[#FBE7E7] bg-[#FBE7E7]/60 hover:bg-[#FBE7E7]'}`}
            data-testid="stock-detail-out-btn"
          >
            <ArrowUp size={12} weight="bold" /> Stock Out
          </button>
        </div>

        {/* Inline movement form */}
        {moveMode && (
          <div className="border border-[#EDE6D8] rounded-lg p-3 space-y-3 bg-[#FCFAF6]/50" data-testid="stock-detail-move-form">
            {moveMode === 'out' && (
              <div className="space-y-1">
                <span className="text-[10px] uppercase tracking-wider text-[#78716C] font-semibold block">Issued To Store</span>
                <Select value={destStoreId} onValueChange={setDestStoreId}>
                  <SelectTrigger className="h-9 text-sm bg-white" data-testid="stock-move-store">
                    <SelectValue placeholder="Select store…" />
                  </SelectTrigger>
                  <SelectContent>
                    {stores.map((s) => (
                      <SelectItem key={s.id} value={s.id}>{s.name}{s.campus_name ? ` · ${s.campus_name}` : ''}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <span className="text-[10px] uppercase tracking-wider text-[#78716C] font-semibold block">Quantity</span>
                <Input
                  type="number"
                  min="1"
                  value={qty}
                  onChange={(e) => setQty(e.target.value)}
                  className="h-9 text-sm font-mono bg-white"
                  data-testid="stock-move-qty"
                />
              </div>
              <div className="space-y-1">
                <span className="text-[10px] uppercase tracking-wider text-[#78716C] font-semibold block">Note (optional)</span>
                <Input
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder={moveMode === 'in' ? 'e.g. Supplier delivery' : 'e.g. Replacement job'}
                  className="h-9 text-sm bg-white"
                  data-testid="stock-move-note"
                />
              </div>
            </div>
            <div className="flex justify-end">
              <Button
                onClick={submit}
                disabled={saving}
                size="sm"
                className="bg-[#0A2540] hover:bg-[#0A2540]/90 text-white rounded-lg h-8"
                data-testid="stock-move-save"
              >
                {saving ? <Spinner size={13} className="animate-spin mr-2" /> : null}
                Confirm {moveMode === 'in' ? 'Stock In' : 'Stock Out'}
              </Button>
            </div>
          </div>
        )}

        {/* Tool movement log */}
        <div>
          <p className="text-[10px] uppercase tracking-[0.18em] text-[#0A2540] font-semibold mb-2" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
            Movement Log
          </p>
          {movesLoading ? (
            <div className="py-6 text-center"><Spinner size={16} className="animate-spin text-[#0A2540] inline" /></div>
          ) : toolMoves.length === 0 ? (
            <p className="text-xs text-[#78716C] py-4 text-center border border-dashed border-[#EDE6D8] rounded-lg">No movements for this tool yet.</p>
          ) : (
            <div className="border border-[#EDE6D8] rounded-lg divide-y divide-[#EDE6D8] max-h-[240px] overflow-y-auto" data-testid="stock-detail-moves">
              {toolMoves.map((m) => (
                <div key={m.id} className="flex items-center justify-between gap-3 px-3 py-2">
                  <div className="min-w-0">
                    <p className="text-sm text-[#1C1917]">
                      <span className={`font-mono font-semibold ${m.direction === 'in' ? 'text-[#047857]' : 'text-[#B91C1C]'}`}>
                        {m.direction === 'in' ? '+' : '−'}{m.quantity}
                      </span>
                      <span className="ml-2 text-[10px] font-mono text-[#78716C]">bal {m.balance_after}</span>
                    </p>
                    <p className="text-[11px] text-[#78716C] truncate">
                      {m.by}{m.direction === 'out' && m.store_name ? ` → ${m.store_name}` : ''}{m.note ? ` — ${m.note}` : ''}
                    </p>
                  </div>
                  <span className="text-xs font-mono text-[#78716C] shrink-0">{fmtDate(m.created_at)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
